import { z } from "zod";

const PERSONAL_COMPOSER_MAX_CHARS = 100_000;
/** How long a save's retry receipt replays. Saved words themselves do not expire (LEA-513). */
export const PERSONAL_COMPOSER_RETENTION_MS = 7 * 24 * 3_600_000;

/**
 * Which box of a conversation a personal draft belongs to (LEA-513, owner decision C8): the reply to
 * the lead (`lead`), or a reply to one message by its inbox id. `forward` is reserved for LEA-514.
 */
const DraftTargetSchema = z.union([
  z.literal("lead"),
  z.string().regex(/^bison:(?:reply|sent_email):[1-9]\d*$/u),
]);
const DraftKindSchema = z.enum(["reply", "forward"]);
export const DraftKeySchema = z.strictObject({
  target: DraftTargetSchema,
  kind: DraftKindSchema,
});
export type DraftKey = z.infer<typeof DraftKeySchema>;
/** The conversation's default draft: the reply to the lead, the box at the thread's top. */
export const LEAD_REPLY: DraftKey = Object.freeze({ target: "lead", kind: "reply" });
/** A reply box above one message (LEA-514): only a lead's reply offers Reply, so only it has one. */
export const MessageReplyTargetSchema = z.string().regex(/^bison:reply:[1-9]\d*$/u);
export const isLeadReply = (draft: DraftKey): boolean =>
  draft.target === LEAD_REPLY.target && draft.kind === LEAD_REPLY.kind;

const ComposerRevisionSchema = z.number().int().nonnegative().max(2_147_483_647);
export const ComposerThreadVersionSchema = z.string().regex(/^tv1:[a-f0-9]{64}$/u);
const Words = z.string().max(PERSONAL_COMPOSER_MAX_CHARS);
const At = z.iso.datetime({ offset: true });

const ComposerSignatureSchema = z.strictObject({
  source: z.enum(["leadcue", "bison"]),
  senderAddress: z.string().max(320),
  html: Words,
  plainText: Words,
  digest: z.string().regex(/^[a-f0-9]{64}$/u),
});

// `body` is text before its explicitly stored signature. `opaque` is the whole editor contents;
// neither server nor client attempts to find a signature boundary inside opaque email text.
export const PersonalComposerContentSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("body"),
    format: z.literal("text"),
    bodyText: Words,
    signature: ComposerSignatureSchema.nullable(),
    plainText: Words,
    html: Words,
  }),
  z.strictObject({
    kind: z.literal("opaque"),
    format: z.enum(["text", "html"]),
    plainText: Words,
    html: Words,
    // Display lineage only. This is never an approval or permission to use the agent send route.
    fromDraft: z.number().int().nonnegative().nullable(),
  }),
]);
export type PersonalComposerContent = z.infer<typeof PersonalComposerContentSchema>;

export const PersonalComposerSchema = z
  .strictObject({
    revision: ComposerRevisionSchema,
    content: PersonalComposerContentSchema.nullable(),
    emptyReason: z.enum(["never_saved", "cleared", "expired", "sent"]).nullable(),
    sourceMessageId: z.string().min(1).max(200).nullable(),
    sourceThreadVersion: ComposerThreadVersionSchema.nullable(),
    updatedAt: At.nullable(),
    expiresAt: At.nullable(),
    lastWriter: z
      .strictObject({
        kind: z.enum(["browser", "mcp"]),
        clientId: z.string().min(1).max(2048),
      })
      .nullable(),
  })
  .refine((value) =>
    value.content === null
      ? value.emptyReason !== null && value.expiresAt === null
      : value.emptyReason === null &&
        value.revision > 0 &&
        value.updatedAt !== null &&
        value.sourceThreadVersion !== null,
  );
export type PersonalComposer = z.infer<typeof PersonalComposerSchema>;

export const PersonalComposerReadSchema = z.strictObject({
  composer: PersonalComposerSchema,
  threadVersion: ComposerThreadVersionSchema,
  sourceMessageId: z.string().min(1).max(200).nullable(),
  observedAt: At,
  appLink: z.url(),
});
export type PersonalComposerRead = z.infer<typeof PersonalComposerReadSchema>;

const Mutation = z.strictObject({
  expectedRevision: ComposerRevisionSchema,
  threadVersion: ComposerThreadVersionSchema,
  sourceMessageId: z.string().min(1).max(200).nullable(),
  idempotencyKey: z.string().min(1).max(200),
});
export const SaveComposerBodySchema = Mutation.extend({
  bodyText: Words,
  // Required for replacing an opaque draft. The client must obtain explicit user confirmation;
  // this assertion is client supplied, not a server attestation that a human clicked a button.
  replaceOpaque: z
    .strictObject({
      revision: ComposerRevisionSchema,
      confirmedByUser: z.literal(true),
    })
    .optional(),
});
export type SaveComposerBody = z.infer<typeof SaveComposerBodySchema>;

const RawRecipients = z.array(z.string().max(320)).max(50);

/**
 * The rest of a whole reply (LEA-513, "Whole reply"): Reply or Reply all, the recipients as the rep
 * left them (unfinished addresses may be kept here, never sent; null while the preset is untouched)
 * and the reply box's file set. Accepted from release A on, stored from release B: a newer tab must
 * still save its words against an older server.
 */
export const ComposerReplySchema = z.strictObject({
  all: z.boolean(),
  recipients: z
    .strictObject({ to: RawRecipients, cc: RawRecipients, bcc: RawRecipients })
    .nullable(),
  files: z.uuid().nullable(),
});
export type ComposerReply = z.infer<typeof ComposerReplySchema>;

export const SaveComposerFullSchema = Mutation.extend({
  format: z.enum(["text", "html"]),
  plainText: Words,
  html: Words,
  fromDraft: z.number().int().nonnegative().nullable(),
  /**
   * Whether the editor holds words the rep wrote, beyond its configured signature and quoted
   * history; the browser knows its own starting state, the server never parses signatures.
   */
  authored: z.boolean().optional(),
  reply: ComposerReplySchema.optional(),
});
export type SaveComposerFull = z.infer<typeof SaveComposerFullSchema>;
export const ClearPersonalComposerSchema = Mutation;
export type ClearPersonalComposer = z.infer<typeof ClearPersonalComposerSchema>;

export const PersonalComposerWriteSchema = z.strictObject({
  replayed: z.boolean(),
  savedRevision: ComposerRevisionSchema,
  savedAt: At,
  retryUntil: At,
  isCurrent: z.boolean(),
  composer: PersonalComposerSchema,
  appLink: z.url(),
  sent: z.literal(false),
});
export type PersonalComposerWrite = z.infer<typeof PersonalComposerWriteSchema>;

const PERSONAL_COMPOSER_REFUSALS = [
  "not_found",
  "forbidden",
  "composer_changed",
  "stale_thread",
  "source_message_not_found",
  "opaque_replacement_required",
  "idempotency_key_reused",
  "rendered_draft_too_large",
  "reply_files_not_found",
  "target_unsupported",
  "conversation_folded",
  "feature_not_available",
  "cursor_invalid",
  "cursor_expired",
  "cursor_context_changed",
] as const;
export type PersonalComposerRefusal = (typeof PERSONAL_COMPOSER_REFUSALS)[number];

/** At most this many conversations per Drafts page. */
export const PERSONAL_DRAFTS_PAGE_SIZE = 50;

export const PersonalDraftListInputSchema = z.strictObject({
  cursor: z.string().min(1).max(4096).optional(),
  limit: z.number().int().min(1).max(PERSONAL_DRAFTS_PAGE_SIZE).default(25),
});
export type PersonalDraftListInput = z.input<typeof PersonalDraftListInputSchema>;

const PersonalDraftSummarySchema = z.strictObject({
  target: DraftTargetSchema,
  kind: DraftKindSchema,
  revision: z.number().int().positive(),
  updatedAt: At,
  /** A bounded start of the draft's own words; never thread mail or recipients. */
  preview: z.string().max(160),
});

/**
 * One conversation in Drafts (owner decision C9): its drafts newest edit first, and how many. The
 * same item serves a draft-only MCP grant, so it carries no mail identity, subject or recipients.
 */
const PersonalDraftConversationSchema = z.strictObject({
  workspaceId: z.string().min(1),
  conversationId: z.string().min(1),
  draftCount: z.number().int().positive(),
  appLink: z.url(),
  drafts: z.array(PersonalDraftSummarySchema).min(1),
});

/** `total` is the Drafts badge: conversations with a draft, independent of the page. */
export const PersonalDraftListSchema = z.strictObject({
  items: z.array(PersonalDraftConversationSchema),
  total: z.number().int().nonnegative(),
  nextCursor: z.string().nullable(),
});
export type PersonalDraftList = z.infer<typeof PersonalDraftListSchema>;

/**
 * A read for the app's own reply box, which asks for it (`?reply=1`): the whole reply beside the
 * words. Never served to MCP or to a request that did not ask, so a draft-only grant never sees
 * recipients and an older tab's strict schema never meets the field.
 */
export const PersonalComposerReadWithReplySchema = PersonalComposerReadSchema.extend({
  reply: ComposerReplySchema.nullable(),
});
export type PersonalComposerReadWithReply = z.infer<typeof PersonalComposerReadWithReplySchema>;
